import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShopError } from '../api';
import { useCustomer } from '../auth/CustomerContext';

/**
 * Ovládání účtu v horním pruhu služeb.
 *
 * Nepřihlášený: tlačítko „Přihlášení" rozbalí mini formulář (e-mail + heslo)
 * a odkaz na registraci; vedle samostatný odkaz „Registrace".
 * Přihlášený: tlačítko se jménem/e-mailem rozbalí nabídku „Můj účet",
 * „Moje objednávky" a „Odhlásit se".
 *
 * Přístupnost jako u ostatních přepínačů v baru: aria-expanded na tlačítku,
 * ovládání klávesnicí, zavření na Esc, blur i ťuknutí mimo. `Dropdown.tsx`
 * je listbox na výběr hodnoty (měna/jazyk), na formulář ani nabídku odkazů
 * nesedí – proto vlastní přístupné řešení.
 */
export function AccountMenu() {
  const { customer, loading } = useCustomer();

  // Dokud nevíme (první /me), nic nekreslíme – ať bar neproblikává mezi
  // „přihlásit" a jménem.
  if (loading && customer === undefined) return null;

  return customer ? <AccountDropdown /> : <GuestControls />;
}

/* ------------------------------------------------------------- nepřihlášený */

function GuestControls() {
  return (
    <div className="account-controls">
      <LoginDropdown />
      <Link className="account-register-link" to="/registrace">
        Registrace
      </Link>
    </div>
  );
}

function LoginDropdown() {
  const { login } = useCustomer();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const baseId = useId();
  const panelId = `${baseId}-panel`;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const close = useCallback((focusButton: boolean) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  useOutsideClose(rootRef, open, () => setOpen(false));

  // Po otevření fokus na první pole.
  useEffect(() => {
    if (open) emailRef.current?.focus();
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password);
      setOpen(false);
      setEmail('');
      setPassword('');
      navigate('/ucet');
    } catch (caught) {
      setError(caught instanceof ShopError ? caught.message : 'Přihlášení se nezdařilo.');
    } finally {
      setSubmitting(false);
    }
  }

  const onPanelKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    }
  };

  return (
    <div
      className="dropdown account-menu"
      ref={rootRef}
      onBlur={(event: ReactFocusEvent<HTMLDivElement>) => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        className="dropdown-toggle"
        ref={buttonRef}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <UserIcon />
        <span className="dropdown-value">Přihlášení</span>
        <span className="dropdown-caret" aria-hidden="true" />
      </button>

      {open && (
        <div
          className="account-panel"
          id={panelId}
          role="dialog"
          aria-label="Přihlášení k účtu"
          onKeyDown={onPanelKeyDown}
        >
          <form className="account-form" onSubmit={submit}>
            <label className="field">
              <span>E-mail</span>
              <input
                ref={emailRef}
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </label>
            <label className="field">
              <span>Heslo</span>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </label>

            {error !== null && (
              <p className="account-error" role="alert">
                {error}
              </p>
            )}

            <button type="submit" className="button primary" disabled={submitting}>
              {submitting ? 'Přihlašuji…' : 'Přihlásit se'}
            </button>
          </form>

          <p className="account-panel-foot">
            Nemáte účet?{' '}
            <Link to="/registrace" onClick={() => setOpen(false)}>
              Registrace
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- přihlášený */

function AccountDropdown() {
  const { customer, logout } = useCustomer();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLAnchorElement>(null);
  const baseId = useId();
  const menuId = `${baseId}-menu`;

  const close = useCallback((focusButton: boolean) => {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }, []);

  useOutsideClose(rootRef, open, () => setOpen(false));

  useEffect(() => {
    if (open) firstItemRef.current?.focus();
  }, [open]);

  async function onLogout() {
    await logout();
    setOpen(false);
    navigate('/');
  }

  const label = customer?.fullName?.trim() || customer?.email || 'Můj účet';

  const onMenuKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    }
  };

  return (
    <div
      className="dropdown account-menu"
      ref={rootRef}
      onBlur={(event: ReactFocusEvent<HTMLDivElement>) => {
        if (!rootRef.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
      }}
    >
      <button
        type="button"
        className="dropdown-toggle"
        ref={buttonRef}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Účet: ${label}`}
      onClick={() => setOpen((value) => !value)}
      >
        <UserIcon />
        <span className="dropdown-value account-name">{label}</span>
        <span className="dropdown-caret" aria-hidden="true" />
      </button>

      {open && (
        <div
          className="account-panel account-menu-list"
          id={menuId}
          role="menu"
          aria-label="Účet"
          onKeyDown={onMenuKeyDown}
        >
          <Link ref={firstItemRef} role="menuitem" to="/ucet" onClick={() => setOpen(false)}>
            Můj účet
          </Link>
          <Link role="menuitem" to="/ucet#objednavky" onClick={() => setOpen(false)}>
            Moje objednávky
          </Link>
          <button type="button" role="menuitem" className="account-logout" onClick={onLogout}>
            Odhlásit se
          </button>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- utily */

/** Zavře panel na ťuknutí/klik mimo kořenový prvek. */
function useOutsideClose(
  rootRef: { current: HTMLElement | null },
  open: boolean,
  onClose: () => void,
) {
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [rootRef, open, onClose]);
}

function UserIcon() {
  return (
    <span className="dropdown-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="14" height="14" focusable="false">
        <circle cx="12" cy="8" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M5 20a7 7 0 0 1 14 0"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}
